<template>
	<div class="grid-flex-container">
		<v-card-text>
			<v-data-table
				:footer-props="{
					itemsPerPageOptions: [10, 15, 20, 25, 30, 50, 100],
					showFirstLastPage: true,
					showCurrentPage: true,
					pagination: {
						page: 1,
						itemsPerPage: 50,
						pageStart,
						pageStop,
						pageCount,
						itemsLength: historyDataCount,
					},
				}"
				:options.sync="options"
				:server-items-length="totalCount"
				:hide-default-footer="loadingData"
				hide-default-header
				fixed-header
				dense
			>
				<template #header>
					<thead class="v-data-table-header">
						<tr>
							<th
								v-for="header in tableColumns"
								:key="header.FieldName"
							>
								{{ header.Label }}
							</th>
							<th class="fixed-col"></th>
						</tr>
						<tr v-if="loadingData" class="v-data-table__progress">
							<th :colspan="tableColumns.length" class="column">
								<v-progress-linear
									indeterminate
								></v-progress-linear>
							</th>
						</tr>
					</thead>
				</template>

				<template #body>
					<tbody v-if="!loadingData">
						<tr
							v-for="(item, id) in historyData"
							:key="item.ActionId"
						>
							<td
								v-for="column in tableColumns"
								:key="column.FieldName"
								:class="hasChanged(id, column.FieldName)"
							>
								<template
									v-if="column.FieldName == 'ActionDate'"
									>{{
										getDateTimeFormat(item.ActionDate)
									}}</template
								>
								<template
									v-else-if="column.FieldName == 'IrisUserId'"
								>
									{{ getUserDisplay(item[column.FieldName]) }}
								</template>
								<template
									v-else-if="column.FieldName == 'Deleted'"
								>
									{{
										String(
											item[column.FieldName]
										)[0].toUpperCase() +
										String(item[column.FieldName]).slice(1)
									}}
								</template>
								<template v-else-if="
										column.FieldName == 'IrisProcess'
									">
									{{ getIrisProcessDisplayFormat(item[column.FieldName]) }}
								</template>
								<template
									v-else-if="
										column.DataType ===
											fieldDataTypes.FILE &&
										item[column.FieldName + '_FileName']
									"
								>
									<v-icon class="mr-2" color="primary"
										>mdi-file-outline</v-icon
									>
									{{ item[column.FieldName + '_FileName'] }}
								</template>
								<template
									v-else-if="
										column.DataType ===
											fieldDataTypes.DATETIME ||
										column.DataType ===
											fieldDataTypes.AUTOGENERATED_DATETIME
									"
								>
									{{
										getDateTimeFormat(
											item[column.FieldName]
										)
									}}
								</template>
								<template
									v-else-if="
										column.DataType === fieldDataTypes.DATE
									"
								>
									{{ getDateFormat(item[column.FieldName]) }}
								</template>
								<template v-else>
									{{ item[column.FieldName] }}
								</template>
							</td>
						</tr>
					</tbody>
				</template>
			</v-data-table>
		</v-card-text>
		<v-card-actions>
			<v-spacer />
			<v-btn text outlined @click="closeEdit()">Close</v-btn>
		</v-card-actions>
	</div>
</template>

<script>
import { FieldDataTypes } from '@/enums/fieldDataTypes';
import { getIrisProcessDisplayFormat } from '../../../enums/processEnum';
import { mapGetters } from 'vuex';
import { InventoryFormHistoryActions } from '../../../store/actions';
import { InventoryFormHistoryGetters } from '../../../store/getters';
import projectSettingsMixin from '../../../mixins/projectSettingsMixin';
import gridViewFieldUtils from '../../../utils/gridViewFieldsUtils';
export default {
	name: 'InventoryHistory',
	props: {
		type: String,
	},
	mixins: [projectSettingsMixin],
	data() {
		return {
			loadingData: false,
			options: {
				itemsPerPage: 50,
				page: 1,
			},
		};
	},
	watch: {
		options() {
			this.initItemHistory();
		},
	},
	computed: {
		currentInventoryFormId() {
			return this.$route.params.formId;
		},
		recordId() {
			return this.$route.params.recordId;
		},
		...mapGetters({
			visibleColumns: [InventoryFormHistoryGetters.GRID_COLUMNS],
			historyData: [InventoryFormHistoryGetters.GRID_DATA],
			historyDataCount: [InventoryFormHistoryGetters.GRID_DATA_COUNT],
			irisUsersMetadata: [InventoryFormHistoryGetters.IRIS_USER_METADATA],
		}),
		fieldDataTypes() {
			return FieldDataTypes;
		},
		totalCount() {
			return this.historyData.length;
		},
		pageStart() {
			return this.options.itemsPerPage * (this.options.page - 1);
		},
		pageStop() {
			return this.options.itemsPerPage * this.options.page;
		},
		pageCount() {
			if (!this.options.itemsPerPage) return 0;
			if (!this.totalCount) return 0;
			let quotient = Math.floor(
				this.totalCount / this.options.itemsPerPage
			);
			let remainder = this.totalCount % this.options.itemsPerPage;
			if (remainder === 0) return quotient;
			return quotient + 1;
		},

		tableColumns() {
			if (!this.visibleColumns && this.visibleColumns.length <= 0)
				return [];

			let columns = this.visibleColumns.map((column) => {
				return {
					Label: column.Label,
					FieldName: gridViewFieldUtils.getFieldName(column),
					DataType: column.DataFieldType,
				};
			});

			return [
				{
					Label: 'Action date',
					FieldName: 'ActionDate',
				},
				{
					Label: 'Iris User',
					FieldName: 'IrisUserId',
				},
				{
					Label: 'Deleted',
					FieldName: 'Deleted',
				},
				{
					Label: 'Process',
					FieldName: 'IrisProcess',
				},
				...columns,
			];
		},
	},
	mounted() {
		this.$store
			.dispatch(
				InventoryFormHistoryActions.LOAD_INVENTORY_FORM,
				this.currentInventoryFormId
			)
			.then((success) => {
				if (success) {
					this.initItemHistory();
				}
			});
	},
	destroyed() {
		this.$store.dispatch(InventoryFormHistoryActions.CLEAR);
	},
	methods: {
		hasChanged(id, column) {
			if (
				column === 'ActionDate' ||
				column === 'IrisUserId' ||
				column === 'IrisProcess'
			)
				return '';

			if (this.historyData.length > id + 1) {
				let newColumn = this.historyData[id][column];
				let oldColumn = this.historyData[id + 1][column];
				if (newColumn !== oldColumn) {
					return 'changed';
				}
			}
			return '';
		},
		initItemHistory() {
			this.loadingData = true;
			let requestData = {
				InventoryFormId: this.currentInventoryFormId,
				RecordId: this.recordId,
				RowsPerPage: this.options.itemsPerPage,
				Page: this.options.page,
			};
			this.$store
				.dispatch(
					InventoryFormHistoryActions.LOAD_GRID_DATA,
					requestData
				)
				.then(() => {
					this.loadingData = false;
				});
		},
		closeEdit() {
			this.$router.go(-1);
			// this.$router.push(this.$route.path.split(`/${this.type}`)[0]);
		},
		getUserDisplay(userId) {
			var user = this.irisUsersMetadata.find((x) => x.Id == userId);
			if (user) {
				return user.IrisUserFormat;
			}
			return userId;
		},
		getIrisProcessDisplayFormat(value) {
			return getIrisProcessDisplayFormat(value);
		},
	},
};
/* mixins:[projectSettingsMixin],
    data() {
        return {
            loadingData: false,
            options: {
                itemsPerPage: 50,
                page: 1,
            },
        };
    },
    watch: {
        options() {
            this.initItemHistory();
        },
    },
    computed: {
        currentInventoryFormId() {
            return this.$route.params.formId;
        },
        recordId() {
            return this.$route.params.recordId;
        },

        ...mapGetters({
            visibleColumns: [InventoryFormHistoryGetters.GRID_COLUMNS],
            historyData:[InventoryFormHistoryGetters.GRID_DATA],
            historyDataCount:[InventoryFormHistoryGetters.GRID_DATA_COUNT]
        }),
        fieldDataTypes() {
            return FieldDataTypes;
        },
        totalCount() {
            return this.historyDataCount;
        },
        pageStart() {
            return this.options.itemsPerPage * (this.options.page - 1);
        },
        pageStop() {
            return this.options.itemsPerPage * this.options.page;
        },
        pageCount() {
            
            if (!this.options.itemsPerPage) return 0;
            if (!this.totalCount) return 0;
            let quotient = Math.floor(
                this.totalCount / this.options.itemsPerPage
            );
            let remainder = this.totalCount % this.options.itemsPerPage;
            if (remainder === 0) return quotient;
            return quotient + 1;
        },

        tableColumns() {
           
                let columns = this.visibleColumns.map((column) => {
                    return {
                        Label: column.Label,
                        FieldName: gridViewFieldUtils.getFieldName(column),
                        DataType: column.DataField.DataType,
                    };
                });

                return [
                    {
                        Label: "Action date",
                        FieldName: "Action_Date",
                    },
                    {
                        Label: "IRIS User first name",
                        FieldName: "Action_UserFirstName",
                    },
                    {
                        Label: "IRIS User last name",
                        FieldName: "Action_UserLastName",
                    },
                    {
                        Label: "IRIS User email",
                        FieldName: "Action_UserEmail",
                    },
                    ...columns,
                ];           
        },
    },
    mounted() {
        this.$store.dispatch(InventoryFormHistoryActions.LOAD_INVENTORY_FORM,this.currentInventoryFormId).then((success)=>{
            if(success){
                this.initItemHistory();
            }
        });
    },
    destroyed(){
        this.$store.dispatch(InventoryFormHistoryActions.CLEAR);
    },
    methods: {
        cancel(){
            this.$router.push({name: "InventoryGrid",params: { formId: this.currentInventoryFormId },});
        },

        hasChanged(id, column) {
            if (
                column === "Action_Date" ||
                column === "Action_UserFirstName" ||
                column === "Action_UserLastName" ||
                column === "Action_UserEmail"
            )
                return "";

            if (this.historyData.length > id + 1) {
                let newColumn = this.historyData[id].Item[column];
                let oldColumn = this.historyData[id + 1].Item[column];
                if (newColumn !== oldColumn) {
                    return "changed";
                }
            }
            return "";
        },
        initItemHistory() {
            this.loadingData = true;
            let requestData = {
                InventoryFormId: this.currentInventoryFormId,
                RecordId: this.recordId,
                RowsPerPage: this.options.itemsPerPage,
                Page: this.options.page,
            };
            this.$store.dispatch(InventoryFormHistoryActions.LOAD_GRID_DATA,requestData).then(()=>{
                this.loadingData = false
            })
        }
    }
}*/
</script>

<style lang="scss">
.grid-flex-container {
	.v-card__text {
		overflow-y: scroll;

		.changed {
			background-color: #ffc00030;
		}

		.grid-field {
			padding: 0 1.5rem;
			margin: 1.5rem 0;
		}

		.v-input.v-input--is-readonly {
			padding: 0 1.5rem;
			margin: 1.5rem 0;
		}
	}
}
</style>
